<template>
  <div class="information-container">
    <div class="header-section">
      <h1>{{ information.title }}</h1>
      <p class="subtitle">{{ information.subtitle }}</p>
    </div>
    
    <div class="content-grid">
      <div class="profile-section">
        <div class="profile-image">
          <img :src="information.profile.image" :alt="information.profile.name">
        </div>
        <div class="profile-info">
          <h2>{{ information.profile.name }}</h2>
          <p class="title">{{ information.profile.title }}</p>
          <div class="contact-info">
            <div v-for="(contact, index) in information.profile.contact" :key="index" class="contact-item">
              <i :class="contact.icon"></i>
              <span>{{ contact.text }}</span>
            </div>
          </div>
          <div class="social-links">
            <a v-for="(social, index) in information.profile.social" 
               :key="index" 
               :href="social.link" 
               class="social-link"
               target="_blank"
               :title="social.label">
              <i :class="social.icon"></i>
            </a>
          </div>
        </div>
      </div>
      <div class="brand-section">
        <h3>About O.Y.I.M.</h3>
        <div class="brand-content">
          <div class="brand-description">
            <div class="brand-acronym">{{ portfolioSection.brand.acronym }}</div>
            <p>{{ portfolioSection.brand.description }}</p>
          </div>
        </div>
      </div>

      <div class="skills-section">
        <h3>Technical Skills</h3>
        <div class="skills-grid">
          <div v-for="(category, index) in information.skills" 
               :key="index" 
               class="skill-category"
               :data-category="category.category">
            <h4>
              <i :class="category.icon"></i>
              {{ category.category }}
            </h4>
            <ul>
              <li v-for="(item, itemIndex) in category.items" :key="itemIndex">
                <span>{{ item.name }}</span>
                <div class="skill-info">
                  <span v-if="item.note" class="skill-note">{{ item.note }}</span>
                  <div v-else class="skill-level">
                    <div class="skill-level-fill" :style="{ width: item.level + '%' }"></div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="experience-section">
        <h3>Professional Experience</h3>
        <div class="timeline">
          <div v-for="(exp, index) in information.experience" :key="index" class="timeline-item">
            <div class="timeline-date">{{ exp.period }}</div>
            <div class="timeline-content">
              <h4>{{ exp.title }}</h4>
              <p class="company">{{ exp.company }}</p>
              <p class="description">{{ exp.description }}</p>
              <div class="details-box">
                <div class="responsibilities">
                  <h5>Responsibilities</h5>
                  <ul>
                    <li v-for="(resp, respIndex) in exp.responsibilities" :key="respIndex">{{ resp }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <HomeButton />
</template>

<script>
import resumeData from '@/data/resumeData.js'
import HomeButton from '@/components/common/HomeButton.vue'

export default {
  name: 'PortfolioInformation',
  components: {
    HomeButton
  },
  data() {
    return {
      information: resumeData.information,
      portfolioSection: resumeData.portfolioSection
    }
  }
}
</script>

<style scoped>
.information-container {
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.header-section {
  text-align: center;
  margin-bottom: 2rem;
}

.header-section h1 {
  font-size: 2.5rem;
  color: #fff;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.1rem;
  color: #666;
}

/* Grid Layout */
.content-grid {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

/* Profile Section */
.profile-section {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2rem;
  align-items: start;
  margin-bottom: 2rem;
}

.profile-image img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.profile-info h2 {
  font-size: 2rem;
  color:#fff;
  margin-bottom: 0.5rem;
}

.title {
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 1.5rem;
}

/* Contact Information */
.contact-info {
  margin-bottom: 1.5rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: #666;
}

.contact-item i {
  color: #73FFF6;
}

/* Social Links */
.social-links {
  display: flex;
  gap: 1rem;
}

.social-link {
  width: 40px;
  height: 40px;
  background: #f0f0f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  transition: all 0.3s ease;
}

.social-link:hover {
  background: #73FFF6;
  color: white;
}

/* Skills Section */
.skills-section {
  background: rgba(255, 255, 255, 0.05);
  padding: 2.5rem 2rem;
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2.5rem;
  width: 100%;
}

.skills-section h3 {
  color: #fff;
  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 600;
}

.skills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  max-width: 1000px;
  margin: 0 auto;
  justify-content: center;
}

.skill-category {
  background: rgba(255, 255, 255, 0.03);
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  margin: 0 auto;
  max-width: 450px;
}

.skill-category:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.skill-category h4 {
  color: #73FFF6;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.skill-category h4 i {
  font-size: 1.8rem;
  color: #73FFF6;
}

.skill-category ul {
  list-style: none;
  padding: 0;
}

.skill-category li {
  color: #fff;
  margin-bottom: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;
}

.skill-category li:hover {
  background: rgba(255, 255, 255, 0.1);
}

.skill-category li:before {
  display: none;
}

.skill-level {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  width: 100px;
  margin-left: 1rem;
  position: relative;
  overflow: hidden;
}

.skill-level-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #73FFF6;
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* Language specific styles */
.skill-category[data-category="Languages"] li {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.skill-note {
  font-size: 0.9rem;
  color: #73FFF6;
  margin-left: 1rem;
}

/* Experience Section */
.experience-section {
  background: rgba(255, 255, 255, 0.05);
  padding: 2.5rem 2rem;
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
}

.experience-section h3 {
  color: #fff;
  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 600;
}

.timeline {
  position: relative;
  padding: 2rem 0;
  margin: 0 2rem;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 2px;
  background: linear-gradient(to bottom,
    rgba(74, 144, 226, 0) 0%,
    rgba(74, 144, 226, 0.5) 10%,
    rgba(74, 144, 226, 0.5) 90%,
    rgba(74, 144, 226, 0) 100%
  );
}

.timeline-item {
  position: relative;
  margin-bottom: 3rem;
  padding-left: 3rem;
}

.timeline-item::before {
  content: '';
  position: absolute;
  left: -9px;
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #1f2029;
  border: 2px solid #73FFF6;
  box-shadow: 0 0 0 4px rgba(74, 144, 226, 0.1);
  transition: all 0.3s ease;
}

.timeline-item:hover::before {
  background: #73FFF6;
  box-shadow: 0 0 0 8px rgba(74, 144, 226, 0.1);
}

.timeline-date {
  position: relative;
  display: inline-block;
  padding: 0.6rem 1.2rem;
  background: linear-gradient(
    135deg,
    rgba(74, 144, 226, 0.2) 0%,
    rgba(74, 144, 226, 0.1) 100%
  );
  border-radius: 25px;
  color: #73FFF6;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
  border: 1px solid rgba(74, 144, 226, 0.2);
  box-shadow: 0 2px 10px rgba(74, 144, 226, 0.1);
  transition: all 0.3s ease;
}

.timeline-date::before {
  content: '';
  position: absolute;
  left: -2.5rem;
  top: 50%;
  width: 1rem;
  height: 1px;
  background: linear-gradient(
    to right,
    rgba(74, 144, 226, 0.5),
    rgba(74, 144, 226, 0)
  );
}

.timeline-item:hover .timeline-date {
  background: linear-gradient(
    135deg,
    rgba(74, 144, 226, 0.3) 0%,
    rgba(74, 144, 226, 0.15) 100%
  );
  transform: translateX(5px);
  box-shadow: 0 4px 15px rgba(74, 144, 226, 0.15);
}

.timeline-content {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.timeline-content:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.03);
}

.timeline-content h4 {
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.company {
  color: #73FFF6;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  font-weight: 500;
  opacity: 0.9;
}

.description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-style: italic;
}

.details-box {
  background: rgba(31, 32, 41, 0.5);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(74, 144, 226, 0.1);
}

.timeline-content h5 {
  color: #73FFF6;
  font-size: 1rem;
  margin-bottom: 1rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.responsibilities ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.responsibilities li {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.8rem;
  padding-left: 1.5rem;
  position: relative;
  line-height: 1.6;
}

.responsibilities li:before {
  content: "•";
  color: #73FFF6;
  position: absolute;
  left: 0.5rem;
  top: 0;
}

.responsibilities li:last-child {
  margin-bottom: 0;
}

.brand-section {
  padding: 2rem;
  border-radius: 8px;
  margin: 2rem 0;
  transition: all 0.3s ease;
}

.brand-section:hover {
  transform: translateY(-2px);
}

.brand-section h3 {
  color: #fff;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.brand-content {
  padding: 1.5rem;
  border-radius: 6px;
}

.brand-description {
  text-align: center;
}

.brand-acronym {
  font-size: 4.5rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 8px;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  background: linear-gradient(45deg, #fff, #e0e0e0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: glow 2s ease-in-out infinite alternate;
  display: inline-block;
  padding: 0.9rem 1rem;
}

.brand-description p {
  color: #e0e0e0;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
  font-weight: 300;
  letter-spacing: 0.3px;
  opacity: 0.8;
}

@keyframes glow {
  from {
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.5),
                 0 0 10px rgba(255, 255, 255, 0.3),
                 0 0 15px rgba(255, 255, 255, 0.2),
                 0 0 20px rgba(255, 255, 255, 0.1);
  }
  to {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.6),
                 0 0 20px rgba(255, 255, 255, 0.4),
                 0 0 30px rgba(255, 255, 255, 0.3),
                 0 0 40px rgba(255, 255, 255, 0.2);
  }
}

@media (max-width: 992px) {
  .header-section h1 {
    font-size: 2.2rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .profile-info h2 {
    font-size: 1.8rem;
  }
  
  .skills-section h3,
  .experience-section h3 {
    font-size: 1.8rem;
  }
  
  .brand-acronym {
    font-size: 3.8rem;
  }
}

@media (max-width: 768px) {
  .information-container {
    padding: 1rem 0.5rem;
  }
  
  .header-section {
    margin-bottom: 1.5rem;
  }
  
  .header-section h1 {
    font-size: 2rem;
  }
  
  .content-grid {
    gap: 2rem;
  }
  
  .profile-section {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .profile-image {
    max-width: 250px;
    margin: 0 auto;
  }
  
  .profile-info {
    text-align: center;
  }
  
  .contact-item {
    justify-content: center;
  }
  
  .social-links {
    justify-content: center;
  }
  
  .skills-section,
  .experience-section {
    width: 100vw;
    margin-left: calc(50% - 50vw);
    border-radius: 0;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  
  .skills-grid {
    gap: 1.5rem;
  }
  
  .skill-category {
    padding: 1.5rem;
    width: 80%;
  }
  
  .timeline {
    margin: 0 1rem;
  }
  
  .timeline-item {
    padding-left: 2rem;
  }
  
  .timeline-content {
    padding: 1.5rem;
  }
  
  .timeline-content h4 {
    font-size: 1.3rem;
  }
  
  .company {
    font-size: 1rem;
  }
  
  .description {
    font-size: 0.95rem;
  }
  
  .details-box {
    padding: 1.2rem;
  }
  
  .timeline-date {
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
  }
  
  .timeline-date::before {
    left: -1.8rem;
    width: 0.8rem;
  }
  
  .brand-section {
    padding: 1.5rem;
    margin: 1.5rem 0;
  }

  .brand-section h3 {
    font-size: 1.5rem;
  }

  .brand-acronym {
    font-size: 3.5rem;
    letter-spacing: 6px;
  }

  .brand-description p {
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .header-section h1 {
    font-size: 1.8rem;
  }
  
  .subtitle {
    font-size: 0.9rem;
  }
  
  .profile-info h2 {
    font-size: 1.5rem;
  }
  
  .title {
    font-size: 1rem;
  }
  
  .skills-section h3,
  .experience-section h3,
  .brand-section h3 {
    font-size: 1.4rem;
  }
  
  .skill-category h4 {
    font-size: 1.2rem;
  }
  
  .skill-category h4 i {
    font-size: 1.5rem;
  }
  
  .skill-category li {
    padding: 0.8rem;
    font-size: 0.9rem;
  }
  
  .timeline-content h4 {
    font-size: 1.2rem;
  }
  
  .company {
    font-size: 0.95rem;
  }
  
  .description {
    font-size: 0.9rem;
  }
  
  .responsibilities li {
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .brand-acronym {
    font-size: 2.8rem;
    letter-spacing: 4px;
  }
  
  .brand-description p {
    font-size: 0.9rem;
  }
}

/* 触摸设备优化 */
@media (hover: none) {
  .skill-category:active {
    transform: translateY(-5px);
  }
  
  .timeline-content:active {
    transform: translateY(-5px);
  }
  
  .brand-section:active {
    transform: translateY(-2px);
  }
  
  .skill-category li {
    background: rgba(255, 255, 255, 0.08);
  }
}
</style> 